import type { Uuid } from "spacetimedb";
import {
  BriefcaseBusinessIcon,
  Building2Icon,
  RedoIcon,
  RulerDimensionLineIcon,
  UndoIcon,
} from "lucide-react";
import { motion } from "motion/react";
import type * as React from "react";
import { useRef } from "react";

import { Button } from "@/web/components/primitives/button";
import { DrawerBody } from "@/web/components/primitives/drawer";
import { Swipable, type SwipableHandle } from "@/web/components/primitives/swipable";
import { motionTransitions } from "@/web/lib/motion-tokens";
import { formatLastSoldLabel, formatLocationLine, formatSqft } from "./formatters";
import type { ReviewComp } from "./types";

export function CompSwipeReview({
  comp,
  exitingCompId,
  remainingCount,
  onSwipeLeft,
  onSwipeRight,
  onExitComplete,
}: {
  comp: ReviewComp | null;
  exitingCompId: Uuid | null;
  remainingCount: number;
  onSwipeLeft: () => void | Promise<void>;
  onSwipeRight: () => void | Promise<void>;
  onExitComplete: () => void;
}) {
  // Imperative handle onto the deck's `Swipable`. The Interested / Not
  // interested buttons below call `swipeRef.current?.swipe(...)` to commit a
  // swipe without a pointer drag — same reducer + exit lifecycle as a real
  // gesture, but no pointer-event timing. The e2e walkthrough drives those same
  // buttons, which avoids the flaky drag/flick simulation
  // (sampled-velocity-dependent) that stalled mid-deck under CI load.
  const swipeRef = useRef<SwipableHandle>(null);

  if (comp == null) {
    return (
      <DrawerBody className="relative flex min-h-0 flex-col items-center overflow-x-hidden px-8 pb-6" />
    );
  }

  // The wrapper outlives any individual comp — it doesn't take a `key`, so
  // it stays mounted across advances and motion can drive a clean fade. The
  // inner `Swipable` is keyed on comp id, so its internal drag state resets
  // when a new comp slides in.
  //   - `await mutation` in the swipe handler holds the card at apex
  //   - on success the parent flips `exitingCompId`, this wrapper fades
  //   - `onSwipeComplete` advances the index after finishExit
  //   - on failure the handler throws → `Swipable.reset()` springs back
  const isExiting = exitingCompId !== null && exitingCompId.compareTo(comp.id) === 0;

  return (
    <DrawerBody className="relative flex min-h-0 flex-col items-center overflow-x-hidden px-8 pb-6">
      <motion.div
        className="relative mt-2 w-full max-w-82"
        initial={false}
        animate={isExiting ? { opacity: 0 } : { opacity: 1 }}
        transition={motionTransitions.cardFade}
      >
        <Swipable
          key={comp.id.toString()}
          ref={swipeRef}
          onSwipeLeft={onSwipeLeft}
          onSwipeRight={onSwipeRight}
          onSwipeComplete={onExitComplete}
          commitProgress={0.6}
        >
          <CompSwipeCard comp={comp} />
        </Swipable>
      </motion.div>

      <p className="mt-7 text-sm text-black/40">{remainingCount} remaining</p>

      {/*
        Tap equivalents for the swipe. These were previously invisible,
        dev-gated e2e-only seams; they're now real UI. Two reasons to surface
        them: this flow had no keyboard path at all (swipe-only, no buttons),
        and the labels state the consequence at the moment of action — a left
        swipe means "reject, gone" here but "skip, comes back" on the action
        card deck, and gesture memory alone can't disambiguate that.

        The swipe hint is merged into the button itself: the tilted arrow
        marks the gesture direction and the "or swipe …" microcopy advertises
        it. (The old standalone hint columns — arrow + hand + "Swipe left/
        right if …" copy stacked above the buttons — were tall enough to push
        the buttons offscreen.) Both buttons are `w-full` so they split the
        row evenly regardless of label length; the Button primitive forwards
        `w-full` to its wrapper span, which is the actual flex child.

        They drive the `Swipable` imperative handle rather than calling the
        handlers directly, so a tap runs the exact same reducer + exit +
        `onSwipeComplete` lifecycle as a real drag, including spring-back on a
        failed approval. (The action-card deck deliberately does the opposite —
        its buttons don't touch `SwipableHandle`, because right-drag is
        suppressed there. Each matches its own gesture model.)

        `data-e2e-comp-swipe` is retained as the e2e selector so the walkthrough
        doesn't depend on user-facing copy.
      */}
      <div className="mt-auto flex w-full items-center gap-3 pt-8">
        <Button
          variant="outline"
          size="md"
          className="w-full"
          data-e2e-comp-swipe="no"
          onClick={() => swipeRef.current?.swipe("left")}
        >
          <UndoIcon className="-rotate-12" aria-hidden />
          <span className="flex flex-col items-start text-left leading-tight">
            Not interested
            <span className="text-xs font-normal opacity-60">or swipe left</span>
          </span>
        </Button>
        <Button
          variant="solid"
          size="md"
          className="w-full"
          data-e2e-comp-swipe="yes"
          onClick={() => swipeRef.current?.swipe("right")}
        >
          <span className="flex flex-col items-end text-right leading-tight">
            Interested
            <span className="text-xs font-normal opacity-60">or swipe right</span>
          </span>
          <RedoIcon className="rotate-12" aria-hidden />
        </Button>
      </div>
    </DrawerBody>
  );
}

function CompSwipeCard({ comp }: { comp: ReviewComp }) {
  const locationLine = formatLocationLine(comp);
  const lastSoldLabel = formatLastSoldLabel(comp);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="flex h-109 flex-col overflow-hidden border-2 border-withus-red bg-white shadow-withus-default"
    >
      <div className="relative h-48 bg-muted">
        {comp.imageUrl ? (
          <img src={comp.imageUrl} alt="" className="size-full object-cover" />
        ) : null}
        <div className="absolute inset-0 bg-black/30" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 p-5">
          <div className="inline-flex flex-col px-2 py-1 font-heading text-base leading-tight text-withus-white uppercase">
            <p>{comp.address.addressLine1}</p>
            <p>{locationLine}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col justify-between p-5">
        <div className="flex flex-col gap-2 text-sm text-withus-dark-blue">
          <CompMetaRow icon={<Building2Icon className="size-3.5" />}>
            {comp.stories ?? "-"} stories, {comp.units ?? "-"} units
          </CompMetaRow>
          <CompMetaRow icon={<RulerDimensionLineIcon className="size-3.5" />}>
            {formatSqft(comp.sqft)} sqft
          </CompMetaRow>
          <CompMetaRow icon={<BriefcaseBusinessIcon className="size-3.5 shrink-0" />}>
            Occupied by <strong>{comp.occupiedBy ?? "Unknown tenant"}</strong>
          </CompMetaRow>
        </div>

        <div className="inline-flex w-fit bg-withus-dark-blue/3 px-2 py-2 text-xs font-semibold tracking-wider text-withus-dark-blue uppercase">
          {lastSoldLabel}
        </div>
      </div>
    </motion.div>
  );
}

function CompMetaRow({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1 text-sm leading-snug">
      {icon}
      <p>{children}</p>
    </div>
  );
}
